import type {
  IActivationCensusValidationErrorsResult,
  IApplyActivationCensusInput,
  IActivationCensusValidationStatusResult,
  ICensusValidationResult,
  ICommercialAddonsResponse,
  ICommercialPlanBundle,
  ICreateQuoteInput,
  IQuote,
  IQuotePriceCalculationInput,
  IQuotePriceCalculationResult,
  IQuoteResponse,
  IQuotesResponse,
  ISendActivationCensusToValidateResult,
  IUpdateQuoteInput,
  IUpdateQuoteStatusInput,
  IUploadQuoteFileResult
} from '../../../interfaces/quotes';
import type { ITemplateResult } from '../../../interfaces/responseInterface';
import {
  APPLY_ACTIVATION_CENSUS_MOCK_RESPONSE,
  CREATE_QUOTE_MOCK_RESPONSE,
  GET_ACTIVATION_CENSUS_VALIDATION_ERRORS_MOCK_RESPONSE,
  GET_ACTIVATION_CENSUS_VALIDATION_STATUS_MOCK_RESPONSE,
  GET_COMMERCIAL_ADDONS_MOCK_RESPONSE,
  GET_COMMERCIAL_PLANS_MOCK_RESPONSE,
  GET_PRICE_CALCULATION_MOCK_RESPONSE,
  GET_QUOTES_MOCK_RESPONSE,
  SEND_ACTIVATION_CENSUS_TO_VALIDATE_MOCK_RESPONSE,
  UPDATE_QUOTE_MOCK_RESPONSE,
  UPDATE_QUOTE_STATUS_MOCK_RESPONSE,
  UPLOAD_QUOTE_FILE_MOCK_RESPONSE,
  GET_QUICK_CENSUS_TEMPLATE_MOCK_RESPONSE,
  VALIDATE_QUICK_CENSUS_MOCK_RESPONSE
} from '../../../mocks/quotes';
import { CensusVariant, type QuoteDocumentType } from '../../../types/quotes';
import { HttpService } from '../../http/httpService';

const QUOTES_PAGE_SIZE = 100;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip "data:<mime>;base64," prefix — API expects raw base64
      resolve(result.split(',')[1] ?? result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export class Quotes {
  static async getQuotes(brokerId: string, prospectId: number, config?: { signal?: AbortSignal }): Promise<IQuote[]> {
    const response = await HttpService.get<IQuotesResponse>(
      `brokers/${brokerId}/prospects/${prospectId}/quotes`,
      { params: { pageSize: QUOTES_PAGE_SIZE }, signal: config?.signal },
      GET_QUOTES_MOCK_RESPONSE()
    );
    return response.data.items;
  }

  static async getCommercialAddons(brokerId: string): Promise<ICommercialAddonsResponse> {
    const response = await HttpService.get<ICommercialAddonsResponse>(
      `brokers/${brokerId}/commercial-addons`,
      {},
      GET_COMMERCIAL_ADDONS_MOCK_RESPONSE()
    );
    return response.data;
  }

  static async getCommercialPlans(brokerId: string): Promise<ICommercialPlanBundle[]> {
    const response = await HttpService.get<ICommercialPlanBundle[]>(
      `brokers/${brokerId}/commercial-plans`,
      {},
      GET_COMMERCIAL_PLANS_MOCK_RESPONSE()
    );
    return response.data;
  }

  static async createQuote(brokerId: string, prospectId: number, body: ICreateQuoteInput): Promise<IQuoteResponse> {
    const response = await HttpService.post<IQuoteResponse>(
      `brokers/${brokerId}/prospects/${prospectId}/quotes`,
      body,
      {},
      CREATE_QUOTE_MOCK_RESPONSE()
    );
    return response.data;
  }

  static async updateQuote(
    brokerId: string,
    prospectId: number,
    quoteId: number,
    body: IUpdateQuoteInput
  ): Promise<IQuote> {
    const response = await HttpService.put<IQuote>(
      `brokers/${brokerId}/prospects/${prospectId}/quotes/${quoteId}`,
      body,
      {},
      UPDATE_QUOTE_MOCK_RESPONSE()
    );
    return response.data;
  }

  static async updateQuoteStatus(
    brokerId: string,
    prospectId: number,
    quoteId: number,
    body: IUpdateQuoteStatusInput
  ): Promise<IQuote> {
    const response = await HttpService.patch<IQuote>(
      `brokers/${brokerId}/prospects/${prospectId}/quotes/${quoteId}/status`,
      body,
      {},
      UPDATE_QUOTE_STATUS_MOCK_RESPONSE()
    );
    return response.data;
  }

  static async getPriceCalculation(
    brokerId: string,
    body: IQuotePriceCalculationInput
  ): Promise<IQuotePriceCalculationResult> {
    const response = await HttpService.post<IQuotePriceCalculationResult>(
      `brokers/${brokerId}/quotes/price-calculations`,
      body,
      {},

      GET_PRICE_CALCULATION_MOCK_RESPONSE()
    );
    return response.data;
  }

  static async getQuickCensusTemplate(brokerId: string): Promise<ITemplateResult> {
    const response = await HttpService.get<ITemplateResult>(
      `brokers/${brokerId}/quick-census/template`,
      {},
      GET_QUICK_CENSUS_TEMPLATE_MOCK_RESPONSE()
    );
    return response.data;
  }

  static async validateQuickCensus(
    brokerId: string,
    file: File,
    minAge?: number,
    maxAge?: number
  ): Promise<ICensusValidationResult> {
    const base64 = await fileToBase64(file);
    const response = await HttpService.post<ICensusValidationResult>(
      `brokers/${brokerId}/quick-census/validations`,
      { base64, ...(minAge !== undefined && { minAge }), ...(maxAge !== undefined && { maxAge }) },
      {},
      VALIDATE_QUICK_CENSUS_MOCK_RESPONSE()
    );
    return response.data;
  }

  static async getCensusTemplate(
    brokerId: string,
    variant: CensusVariant = CensusVariant.Insurance
  ): Promise<ITemplateResult> {
    const response = await HttpService.get<ITemplateResult>(
      `brokers/${brokerId}/census/template`,
      { params: { variant } },
      GET_QUICK_CENSUS_TEMPLATE_MOCK_RESPONSE()
    );
    return response.data;
  }

  static async sendActivationCensusToValidate(
    brokerId: string,
    prospectId: number,
    quoteId: number,
    file: File,
    variant: CensusVariant = CensusVariant.Insurance
  ): Promise<ISendActivationCensusToValidateResult> {
    const base64 = await fileToBase64(file);
    const response = await HttpService.post<ISendActivationCensusToValidateResult>(
      `brokers/${brokerId}/prospects/${prospectId}/quotes/${quoteId}/census/validations`,
      { base64, variant },
      {},
      SEND_ACTIVATION_CENSUS_TO_VALIDATE_MOCK_RESPONSE()
    );
    return response.data;
  }

  static async uploadQuoteFile(
    brokerId: string,
    prospectId: number,
    quoteId: number,
    file: File,
    documentType?: QuoteDocumentType
  ): Promise<IUploadQuoteFileResult> {
    const base64 = await fileToBase64(file);
    const response = await HttpService.post<IUploadQuoteFileResult>(
      `brokers/${brokerId}/prospects/${prospectId}/quotes/${quoteId}/files`,
      { base64, fileName: file.name, ...(documentType ? { documentType } : {}) },
      {},
      UPLOAD_QUOTE_FILE_MOCK_RESPONSE(file.name, documentType)
    );
    return response.data;
  }

  static async getActivationCensusValidationStatus(
    brokerId: string,
    requestId: string
  ): Promise<IActivationCensusValidationStatusResult> {
    const response = await HttpService.get<IActivationCensusValidationStatusResult>(
      `brokers/${brokerId}/census/validations/${requestId}/status`,
      {},
      GET_ACTIVATION_CENSUS_VALIDATION_STATUS_MOCK_RESPONSE()
    );
    return response.data;
  }

  static async getActivationCensusValidationErrors(
    brokerId: string,
    requestId: string,
    variant: CensusVariant = CensusVariant.Insurance
  ): Promise<IActivationCensusValidationErrorsResult> {
    const response = await HttpService.get<IActivationCensusValidationErrorsResult>(
      `brokers/${brokerId}/census/validations/${requestId}/errors`,
      { params: { variant } },
      GET_ACTIVATION_CENSUS_VALIDATION_ERRORS_MOCK_RESPONSE()
    );
    return response.data;
  }

  static async applyActivationCensus(
    brokerId: string,
    prospectId: number,
    quoteId: number,
    body: IApplyActivationCensusInput
  ): Promise<IQuote> {
    const response = await HttpService.post<IQuote>(
      `brokers/${brokerId}/prospects/${prospectId}/quotes/${quoteId}/census/apply`,
      body,
      {},
      APPLY_ACTIVATION_CENSUS_MOCK_RESPONSE()
    );
    return response.data;
  }
}

export default Quotes;
